/* eslint-disable react/jsx-props-no-spreading */
import Dynamic from '@base/Dynamic/Dynamic';
import CarouselFeedBlock from '@cont/FeedTypeBlock/CarouselFeedBlock';
import EducatorFeedBlock from '@cont/FeedTypeBlock/EducatorFeedBlock';
import ClassFeedBlock from '@cont/FeedTypeBlock/ClassFeedBlock';
import TestSeriesFeedBlock from '@cont/FeedTypeBlock/TestSeriesFeedBlock';
import PopularSubjects from '@cont/FeedTypeBlock/PopularSubjects';
import useExperiments from '@stores/experiments';
import { getExperimentVariation } from '@stores/selectors/experiments';
import { getABExperimentBucketing } from '@cont/NewPreSubscribed/utils';
import UpcomingFeedBlock from '@cont/FeedTypeBlock/UpcomingClassBlock';
import CuratedPlayList from '@cont/FeedTypeBlock/CuratedPlayList';
import {
  isMyDownloadsType,
  isLegendsOnUnacademyType,
  isGenericBanner,
  isCarouselEntityType,
  isSubjectEntityType,
  isEducatorEntityType,
  isCourseEntityType,
  isBannerEntityType,
  isTopologyEntityType,
  isTestSeriesEntityType,
  isExternalSubscriptionEntityType,
  isCuratedPlaylistEntityType,
  isUpcomingClassFeed
} from './utils/getBlockType';

const LegendsBannerBlock = Dynamic(() =>
  import('@cont/FeedTypeBlock/LegendsBannerBlock')
);
const TopicFeedBlock = Dynamic(() =>
  import('@cont/FeedTypeBlock/TopicFeedBlock')
);
const BatchFeedBlock = Dynamic(() =>
  import('@cont/FeedTypeBlock/BatchFeedBlock')
);
const ActivateSubscription = Dynamic(() =>
  import('@cont/FeedTypeBlock/ActivateSubscription')
);
const GenericBannerBlock = Dynamic(() =>
  import('@cont/FeedTypeBlock/GenericBannerBlock')
);

const FeedTypeBlock = ({ feedBlock, ...extraProps }) => {
  const feedType = feedBlock.type;
  const feedEntityType = feedBlock.extraBlockinfo.entityType;

  const experimentVariation = useExperiments(
    getExperimentVariation('notify_me_and_follow_educator_component')
  );
  const { showUpcomingClass } = getABExperimentBucketing(experimentVariation);

  if (isMyDownloadsType(feedType)) {
    return null;
  }

  if (isGenericBanner(feedType)) {
    return <GenericBannerBlock block={feedBlock} />;
  }

  if (isLegendsOnUnacademyType(feedType)) {
    return <LegendsBannerBlock block={feedBlock} {...extraProps} />;
  }

  if (isSubjectEntityType(feedEntityType)) {
    return <PopularSubjects feedBlock={feedBlock} />;
  }

  if (isEducatorEntityType(feedEntityType)) {
    return <EducatorFeedBlock educatorFeedBlock={feedBlock} {...extraProps} />;
  }

  if (
    isUpcomingClassFeed(feedType) &&
    showUpcomingClass &&
    isCourseEntityType(feedEntityType)
  )
    return <UpcomingFeedBlock classFeedBlock={feedBlock} {...extraProps} />;

  if (isCourseEntityType(feedEntityType))
    return <ClassFeedBlock classFeedBlock={feedBlock} {...extraProps} />;

  if (isBannerEntityType(feedEntityType))
    return <BatchFeedBlock feedBlock={feedBlock} {...extraProps} />;

  if (isTopologyEntityType(feedEntityType))
    return (
      <TopicFeedBlock
        topicFeedBlock={feedBlock}
        topicType={feedBlock.extraBlockinfo?.type}
        {...extraProps}
      />
    );

  if (isTestSeriesEntityType(feedEntityType))
    return (
      <TestSeriesFeedBlock
        testSeriesFeedBlock={feedBlock}
        feedBlocktype={extraProps.type}
        {...extraProps}
      />
    );

  if (isCarouselEntityType(feedEntityType)) {
    return <CarouselFeedBlock block={feedBlock} {...extraProps} />;
  }

  if (isExternalSubscriptionEntityType(feedType)) {
    return <ActivateSubscription feedBlock={feedBlock} {...extraProps} />;
  }

  if (isCuratedPlaylistEntityType(feedEntityType)) {
    return <CuratedPlayList block={feedBlock} />;
  }

  return null;
};

export default FeedTypeBlock;
